import { useRef, useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import {
  X,
  User,
  LogOut,
  Bell,
  Gift,
  Megaphone,
  LifeBuoy,
  Accessibility,
  ChevronRight,
  Loader2,
  TrendingUp,
  RefreshCw,
  Gem,
} from "lucide-react";
import { useAppSelector } from "@/app/hooks";
import { selectAuth } from "@/features/auth/authSlice";
import { logoutAndRedirect } from "@/lib/api/authService";
import { showApiError, showSuccess } from "@/lib/utils/notifications";
import {
  fetchUserNotifications,
  fetchUserNotificationDetail,
  markAllNotificationsRead,
  type Notification,
  type NotificationDetail,
} from "@/lib/api/notificationService";
import { NotificationDetailPanel } from "./NotificationDetailPanel";
import styles from "../styles/userDrawer.module.css";
import { NOTIFICATION_MENTION_SUBJECT } from "../utils/notificationDetailHelpers";
import "@/features/system-announcement/styles/announcementHtml.css";
import { normalizeAnnouncementDescriptionHtml } from "@/features/system-announcement/utils/normalizeAnnouncementHtml";
import {
  fetchReleaseNotes,
  markReleaseNoteRead,
  type ReleaseNote,
} from "@/lib/api/releaseNotesService";
import {
  fetchSystemAnnouncements,
  markAnnouncementRead,
  type Announcement,
} from "@/lib/api/announcementsService";
import {
  fetchNotificationCounts,
  type NotificationCounts,
} from "@/lib/api/notificationCountService";
import { useUserRole } from "@/hooks/useUserRole";
import {
  VERSION_TYPE_LABELS,
  VERSION_TYPE_COLOR_CLASS,
  buildNotificationPreviewText,
  getReleaseNoteListPreview,
  formatNotificationScheduleDate,
  hasUnreadNotifications,
  isNotificationUnread,
  isReleaseNoteUnread,
  isAnnouncementUnread,
  getDrawerListItemBackgroundClass,
  getUserInitials,
  notificationCreatedAtDate,
} from "../utils/userDrawerHelpers";
import { format } from "date-fns";
import type { UserDrawerProps } from "../types/components";

export function UserDrawer({ isOpen, onClose, onCountsRefresh }: UserDrawerProps) {
  const drawerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [avatarBroken, setAvatarBroken] = useState(false);
  const { user } = useAppSelector(selectAuth);

  const { isLibertyRole } = useUserRole();

  const handleLogoutClick = () => {
    setShowLogoutModal(true);
  };

  const handleLogoutConfirm = async () => {
    setIsLoggingOut(true);
    // Same as applicant logout: do not clear Redux auth before redirect —
    // logoutAndRedirect handles cleanup + navigates to login.
    await logoutAndRedirect();
  };

  const handleLogoutCancel = () => {
    if (isLoggingOut) return;
    setShowLogoutModal(false);
  };
  const [view, setView] = useState<
    "menu" | "notifications" | "whats_new" | "announcements"
  >("menu");
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [selectedNotificationMail, setSelectedNotificationMail] =
    useState<Notification | null>(null);
  const [notificationDetail, setNotificationDetail] =
    useState<NotificationDetail | null>(null);
  const [loadingNotificationDetail, setLoadingNotificationDetail] =
    useState(false);
  const [releaseNotes, setReleaseNotes] = useState<ReleaseNote[]>([]);
  const [selectedReleaseNote, setSelectedReleaseNote] =
    useState<ReleaseNote | null>(null);
  const [loadingReleaseNotes, setLoadingReleaseNotes] = useState(false);
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [selectedAnnouncement, setSelectedAnnouncement] =
    useState<Announcement | null>(null);
  const [loadingAnnouncements, setLoadingAnnouncements] = useState(false);
  const [notificationCounts, setNotificationCounts] =
    useState<NotificationCounts | null>(null);

  const loadNotificationCounts = useCallback(async () => {
    try {
      const counts = await fetchNotificationCounts();
      setNotificationCounts(counts);
      await onCountsRefresh?.();
    } catch (error: unknown) {
      console.error("Failed to fetch notification counts", error);
    }
  }, [onCountsRefresh]);

  useEffect(() => {
    setAvatarBroken(false);
  }, [user?.profile_pic]);

  // Reset view when closed; load badge counts when opened
  useEffect(() => {
    if (!isOpen) {
      setView("menu");
      setAvatarBroken(false);
      setNotifications([]);
      setSelectedNotificationMail(null);
      setNotificationDetail(null);
      setLoadingNotifications(false);
      setLoadingNotificationDetail(false);
      setReleaseNotes([]);
      setSelectedReleaseNote(null);
      setLoadingReleaseNotes(false);
      setAnnouncements([]);
      setSelectedAnnouncement(null);
      setLoadingAnnouncements(false);
      return;
    }
    void loadNotificationCounts();
  }, [isOpen, loadNotificationCounts]);

  // Refresh badge counts while drawer stays open (Angular notificationupdate subscription)
  useEffect(() => {
    if (!isOpen) return;

    const intervalId = window.setInterval(() => {
      void loadNotificationCounts();
    }, 30_000);

    const handleFocus = () => {
      void loadNotificationCounts();
    };
    window.addEventListener("focus", handleFocus);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("focus", handleFocus);
    };
  }, [isOpen, loadNotificationCounts]);

  // Release note image preview (matches Angular right-drawer filePreview)
  useEffect(() => {
    if (view !== "whats_new") return;

    const handleImagePreview = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof HTMLImageElement)) return;
      if (target.getAttribute("alt") !== "filepreviewpopup") return;
      const src = target.getAttribute("src");
      if (!src) return;
      window.open(
        `/file-handlers?path=${encodeURIComponent(src)}`,
        "_blank",
        "noopener",
      );
    };

    document.addEventListener("click", handleImagePreview);
    return () => document.removeEventListener("click", handleImagePreview);
  }, [view]);

  const reloadNotifications = useCallback(async () => {
    if (!user?.user_id) return;
    try {
      const response = await fetchUserNotifications(user.user_id, 100);
      setNotifications(response.data ?? []);
    } catch (error: unknown) {
      showApiError(error);
    }
  }, [user?.user_id]);

  const handleShowNotifications = async () => {
    setView("notifications");
    setSelectedNotificationMail(null);
    setNotificationDetail(null);
    if (!user?.user_id) {
      setNotifications([]);
      return;
    }

    setLoadingNotifications(true);
    try {
      const response = await fetchUserNotifications(user.user_id, 100);
      setNotifications(response.data ?? []);
    } catch (error: unknown) {
      showApiError(error);
      setNotifications([]);
    } finally {
      setLoadingNotifications(false);
    }
  };

  const handleShowWhatsNew = async () => {
    setView("whats_new");
    setSelectedReleaseNote(null);
    setLoadingReleaseNotes(true);
    try {
      const response = await fetchReleaseNotes();
      setReleaseNotes(response.data ?? []);
    } catch (error: unknown) {
      showApiError(error);
      setReleaseNotes([]);
    } finally {
      setLoadingReleaseNotes(false);
    }
  };

  const reloadReleaseNotes = useCallback(async () => {
    try {
      const response = await fetchReleaseNotes();
      setReleaseNotes(response.data ?? []);
    } catch (error: unknown) {
      showApiError(error);
    }
  }, []);

  const handleReleaseNoteSelect = async (note: ReleaseNote) => {
    setSelectedReleaseNote(note);
    if (isReleaseNoteUnread(note)) {
      try {
        await markReleaseNoteRead(note.id);
        setReleaseNotes((prev) =>
          prev.map((item) =>
            item.id === note.id ? { ...item, is_read: "1" } : item,
          ),
        );
        void loadNotificationCounts();
      } catch (error: unknown) {
        showApiError(error);
      }
    }
  };

  const handleMarkAllNotificationsRead = async () => {
    try {
      await markAllNotificationsRead();
      await reloadNotifications();
      void loadNotificationCounts();
      showSuccess("All notifications marked 'Read' successfully!");
    } catch (error: unknown) {
      showApiError(error);
    }
  };

  const handleNotificationSelect = async (notification: Notification) => {
    if (loadingNotificationDetail) return;

    setSelectedNotificationMail(notification);
    setLoadingNotificationDetail(true);
    try {
      const detail = await fetchUserNotificationDetail(
        notification.notification_id,
      );
      setNotificationDetail(detail);
      setSelectedNotificationMail({ ...notification, ...detail });
      setNotifications((prev) =>
        prev.map((item) =>
          item.notification_id === notification.notification_id
            ? { ...item, is_read: "1" }
            : item,
        ),
      );
      void loadNotificationCounts();
    } catch (error: unknown) {
      showApiError(error);
      setSelectedNotificationMail(null);
      setNotificationDetail(null);
    } finally {
      setLoadingNotificationDetail(false);
    }
  };

  const handleNotificationDetailBack = () => {
    setSelectedNotificationMail(null);
    setNotificationDetail(null);
    setLoadingNotificationDetail(false);
    void reloadNotifications();
  };

  const reloadAnnouncements = useCallback(async () => {
    try {
      const response = await fetchSystemAnnouncements();
      setAnnouncements(response.data ?? []);
    } catch (error: unknown) {
      showApiError(error);
    }
  }, []);

  const handleAnnouncementSelect = (announcement: Announcement) => {
    setSelectedAnnouncement(announcement);
    if (announcement.is_read !== 1) {
      void markAnnouncementRead(announcement.sa_id)
        .then(() => {
          setAnnouncements((prev) =>
            prev.map((item) =>
              item.sa_id === announcement.sa_id
                ? { ...item, is_read: 1 }
                : item,
            ),
          );
          setSelectedAnnouncement((prev) =>
            prev?.sa_id === announcement.sa_id ? { ...prev, is_read: 1 } : prev,
          );
          void loadNotificationCounts();
        })
        .catch((error: unknown) => showApiError(error));
    }
  };

  const handleDrawerBack = () => {
    if (
      view === "notifications" &&
      (notificationDetail ||
        selectedNotificationMail ||
        loadingNotificationDetail)
    ) {
      handleNotificationDetailBack();
      return;
    }
    if (view === "whats_new" && selectedReleaseNote) {
      setSelectedReleaseNote(null);
      void reloadReleaseNotes();
      return;
    }
    if (view === "announcements" && selectedAnnouncement) {
      setSelectedAnnouncement(null);
      void reloadAnnouncements();
      return;
    }
    setView("menu");
    void loadNotificationCounts();
  };

  const handleShowAnnouncements = async () => {
    setView("announcements");
    setSelectedAnnouncement(null);
    setLoadingAnnouncements(true);
    try {
      const response = await fetchSystemAnnouncements();
      setAnnouncements(response.data ?? []);
    } catch (error: unknown) {
      showApiError(error);
      setAnnouncements([]);
    } finally {
      setLoadingAnnouncements(false);
    }
  };

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Don't close if logging out or confirm dialog is open
      if (isLoggingOut || showLogoutModal) return;

      if (
        drawerRef.current &&
        !drawerRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose, isLoggingOut, showLogoutModal]);

  // Keep mounted while logout confirm is open so the dialog is not unmounted with the drawer
  if (!isOpen && !showLogoutModal) return null;

  return (
    <div
      data-testid="invitee-user-drawer-root"
      className={
        isOpen
          ? "fixed inset-0 z-[9999] overflow-hidden"
          : "fixed inset-0 z-[9999] pointer-events-none"
      }
    >
      {/* Overlay — only when drawer is open */}
      {isOpen ? (
        <div
          data-testid="invitee-user-drawer-backdrop"
          className="absolute inset-0 bg-black/20 backdrop-blur-sm transition-opacity pointer-events-auto"
        />
      ) : null}

      {/* Drawer Panel */}
      {isOpen ? (
      <div
        ref={drawerRef}
        data-testid="invitee-user-drawer-panel"
        className={`absolute inset-y-0 right-0 flex w-full max-w-[400px] transform flex-col overflow-hidden bg-white shadow-2xl transition-transform duration-300 ease-in-out pointer-events-auto ${styles.drawerPanel}`}
      >
        {/* Header Section — close on the right so it never overlaps the mobile nav hamburger */}
        <div
          data-testid="invitee-user-drawer-header"
          className="flex min-h-[72px] items-center justify-between gap-3 border-b border-slate-100 bg-gradient-to-b from-slate-50 to-white px-4 py-3 sm:px-6"
        >
          {view !== "menu" ? (
            <button
              type="button"
              data-testid="invitee-user-drawer-back-to-menu-button"
              onClick={handleDrawerBack}
              className="flex min-w-0 items-center gap-2 text-slate-500 transition-colors hover:text-slate-900"
            >
              <ChevronRight className="h-5 w-5 shrink-0 rotate-180" />
              {view === "notifications" && (
                <>
                  <Bell className="h-5 w-5 shrink-0 text-[rgb(21_151_255)] fill-[rgb(21_151_255)]/10" />
                  <span className="truncate font-bold text-lg text-slate-900">
                    Notifications
                  </span>
                </>
              )}
              {view === "whats_new" && (
                <>
                  <Gift className="h-5 w-5 shrink-0 text-green-500" />
                  <span className="truncate font-bold text-lg text-slate-900">
                    What's New
                  </span>
                </>
              )}
              {view === "announcements" && (
                <>
                  <Megaphone className="h-5 w-5 shrink-0 text-amber-500" />
                  <span className="truncate font-bold text-lg text-slate-900">
                    Announcements
                  </span>
                </>
              )}
            </button>
          ) : (
            <p className="text-base font-bold text-slate-900">
              Profile
            </p>
          )}

          <button
            type="button"
            data-testid="invitee-user-drawer-close-button"
            onClick={onClose}
            disabled={isLoggingOut}
            className="ml-auto shrink-0 rounded-full border border-slate-200 bg-white p-2 text-slate-500 shadow-sm transition hover:bg-slate-50 hover:text-slate-800 disabled:opacity-50"
            aria-label="Close menu"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content Area */}
        <div
          data-testid="invitee-user-drawer-content"
          className="relative flex-1 overflow-y-auto bg-slate-50/60"
        >
          {view === "menu" ? (
            <div data-testid="invitee-user-drawer-menu" className="space-y-4 p-4 pb-6 sm:p-5">
              <div
                data-testid="invitee-user-drawer-profile-section"
                className="rounded-3xl border border-slate-200/80 bg-white p-5 shadow-sm"
              >
                <div className="flex items-center gap-3.5">
                  <div className="relative shrink-0">
                    <div
                      data-testid="invitee-user-drawer-avatar"
                      className="flex h-14 w-14 items-center justify-center overflow-hidden rounded-full border border-slate-200 bg-gradient-to-br from-sky-100 to-slate-100 text-base font-bold tracking-wide text-sky-700 shadow-sm"
                    >
                      {user?.profile_pic && !avatarBroken ? (
                        <img
                          src={user.profile_pic}
                          alt=""
                          className="h-full w-full object-cover"
                          onError={() => setAvatarBroken(true)}
                        />
                      ) : (
                        <span data-testid="invitee-user-drawer-avatar-initials">
                          {getUserInitials(user?.first_name, user?.last_name)}
                        </span>
                      )}
                    </div>
                    <span className="absolute bottom-0 right-0 h-3.5 w-3.5 rounded-full border-2 border-white bg-emerald-500" />
                  </div>

                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-slate-500">Hello</p>
                    <h3 className="truncate text-lg font-bold tracking-tight text-slate-900">
                      {user?.first_name} {user?.last_name}
                    </h3>
                  </div>
                </div>

                <div className="mt-4 flex gap-2.5">
                  <button
                    type="button"
                    data-testid="invitee-user-drawer-account-button"
                    className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-full bg-[rgb(21_151_255)] px-3 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#0d7acc]"
                    onClick={() => {
                      onClose();
                      navigate("/user/profile");
                    }}
                  >
                    <User className="h-4 w-4" />
                    Account
                  </button>
                  <button
                    type="button"
                    data-testid="invitee-user-drawer-logout-button"
                    className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-full border border-rose-300 bg-white px-3 py-2.5 text-sm font-semibold text-rose-500 transition hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-50"
                    disabled={isLoggingOut}
                    onClick={handleLogoutClick}
                  >
                    <LogOut className="h-4 w-4" />
                    Logout
                  </button>
                </div>
              </div>

              <div className="space-y-2.5">
                {/* My Notifications (Liberty roles only — matches Angular) */}
                {isLibertyRole && (
                  <button
                    type="button"
                    data-testid="invitee-user-drawer-notifications-button"
                    className="group flex w-full items-start gap-3 rounded-2xl border border-slate-200/80 bg-white p-3.5 text-left shadow-sm transition hover:border-sky-200 hover:shadow-md"
                    onClick={handleShowNotifications}
                  >
                    <div className="relative shrink-0">
                      <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-sky-50 text-[rgb(21_151_255)]">
                        <Bell className="h-5 w-5" strokeWidth={1.75} />
                      </span>
                      {notificationCounts?.notification ? (
                        <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full border-2 border-white bg-rose-500 px-1 text-[10px] font-bold text-white">
                          {notificationCounts.notification}
                        </span>
                      ) : null}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <h4 className="text-sm font-bold text-slate-900 transition group-hover:text-[rgb(21_151_255)]">
                          My Notifications
                        </h4>
                        <ChevronRight className="h-4 w-4 shrink-0 text-slate-300 transition group-hover:text-[rgb(21_151_255)]" />
                      </div>
                      <p className="mt-0.5 text-xs leading-relaxed text-slate-500">
                        System, user, and Liberty Rent notifications and action items.
                      </p>
                    </div>
                  </button>
                )}

                <button
                  type="button"
                  data-testid="invitee-user-drawer-whats-new-button"
                  className="group flex w-full items-start gap-3 rounded-2xl border border-slate-200/80 bg-white p-3.5 text-left shadow-sm transition hover:border-emerald-200 hover:shadow-md"
                  onClick={handleShowWhatsNew}
                >
                  <div className="relative shrink-0">
                    <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600">
                      <Gift className="h-5 w-5" strokeWidth={1.75} />
                    </span>
                    {notificationCounts?.release_notes ? (
                      <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full border-2 border-white bg-rose-500 px-1 text-[10px] font-bold text-white">
                        {notificationCounts.release_notes}
                      </span>
                    ) : null}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <h4 className="text-sm font-bold text-slate-900 transition group-hover:text-emerald-600">
                        What's New
                      </h4>
                      <ChevronRight className="h-4 w-4 shrink-0 text-slate-300 transition group-hover:text-emerald-500" />
                    </div>
                    <p className="mt-0.5 text-xs leading-relaxed text-slate-500">
                      Latest features and improvements from the Liberty Rent team.
                    </p>
                  </div>
                </button>

                <button
                  type="button"
                  data-testid="invitee-user-drawer-announcements-button"
                  className="group flex w-full items-start gap-3 rounded-2xl border border-slate-200/80 bg-white p-3.5 text-left shadow-sm transition hover:border-amber-200 hover:shadow-md"
                  onClick={handleShowAnnouncements}
                >
                  <div className="relative shrink-0">
                    <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-amber-50 text-amber-600">
                      <Megaphone className="h-5 w-5" strokeWidth={1.75} />
                    </span>
                    {notificationCounts?.system_announcement ? (
                      <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full border-2 border-white bg-rose-500 px-1 text-[10px] font-bold text-white">
                        {notificationCounts.system_announcement}
                      </span>
                    ) : null}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <h4 className="text-sm font-bold text-slate-900 transition group-hover:text-amber-600">
                        System Announcements
                      </h4>
                      <ChevronRight className="h-4 w-4 shrink-0 text-slate-300 transition group-hover:text-amber-500" />
                    </div>
                    <p className="mt-0.5 text-xs leading-relaxed text-slate-500">
                      Holiday hours, scheduled maintenance, and outage info.
                    </p>
                  </div>
                </button>

                <button
                  type="button"
                  data-testid="invitee-user-drawer-help-center-button"
                  className="group flex w-full items-start gap-3 rounded-2xl border border-slate-200/80 bg-white p-3.5 text-left shadow-sm transition hover:border-rose-200 hover:shadow-md"
                  onClick={() => {
                    onClose();
                    navigate("/help-about");
                  }}
                >
                  <div className="relative shrink-0">
                    <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-rose-50 text-rose-500">
                      <LifeBuoy className="h-5 w-5" strokeWidth={1.75} />
                    </span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <h4 className="text-sm font-bold text-slate-900 transition group-hover:text-rose-500">
                        Help Center
                      </h4>
                      <ChevronRight className="h-4 w-4 shrink-0 text-slate-300 transition group-hover:text-rose-400" />
                    </div>
                    <p className="mt-0.5 text-xs leading-relaxed text-slate-500">
                      Guides, tutorials, and knowledgebase articles.
                    </p>
                  </div>
                </button>
              </div>
            </div>
          ) : view === "whats_new" ? (
            /* Release Notes List / Detail */
            <div
              data-testid="invitee-user-drawer-whats-new-view"
              className="absolute inset-0 overflow-y-auto bg-gray-50/50"
            >
              <div className="bg-white px-4 py-3 border-b border-gray-100 sticky top-0 z-10 shadow-sm">
                <div className="flex items-center justify-between">
                  <h4 className="font-bold text-gray-900">Release Notes</h4>
                  <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                    {releaseNotes.length} Updates
                  </span>
                </div>
              </div>

              {loadingReleaseNotes ? (
                <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                  <Loader2 className="h-8 w-8 animate-spin mb-2" />
                  <p>Loading updates...</p>
                </div>
              ) : selectedReleaseNote ? (
                <div className="p-4">
                  <button
                    type="button"
                    data-testid="invitee-user-drawer-release-note-back"
                    onClick={() => {
                      setSelectedReleaseNote(null);
                      void reloadReleaseNotes();
                    }}
                    className="mb-4 flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900"
                  >
                    <ChevronRight className="h-4 w-4 rotate-180" />
                    Back to list
                  </button>
                  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
                    <div className="flex items-start gap-3">
                      {selectedReleaseNote.version_type === "PROCESS_IMPROVEMENT" && (
                        <TrendingUp className="h-6 w-6 text-blue-600 shrink-0" />
                      )}
                      {selectedReleaseNote.version_type === "PROCESS_CHANGE" && (
                        <RefreshCw className="h-6 w-6 text-amber-600 shrink-0" />
                      )}
                      {selectedReleaseNote.version_type === "NEW_FEATURE" && (
                        <Gem className="h-6 w-6 text-green-600 shrink-0" />
                      )}
                      <div>
                        <h5 className="font-bold text-gray-900">
                          {selectedReleaseNote.version_title}
                        </h5>
                        <p
                          className={`text-sm font-medium ${VERSION_TYPE_COLOR_CLASS[selectedReleaseNote.version_type] ?? "text-gray-600"}`}
                        >
                          {VERSION_TYPE_LABELS[selectedReleaseNote.version_type] ??
                            selectedReleaseNote.version_type}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {selectedReleaseNote.version_date
                            ? format(
                                new Date(selectedReleaseNote.version_date),
                                "MMM dd, yyyy",
                              )
                            : ""}
                        </p>
                      </div>
                    </div>
                    {selectedReleaseNote.version_description && (
                      <div
                        className="mt-4 text-sm text-gray-700 prose prose-sm max-w-none"
                        dangerouslySetInnerHTML={{
                          __html: selectedReleaseNote.version_description,
                        }}
                      />
                    )}
                  </div>
                </div>
              ) : releaseNotes.length > 0 ? (
                <div className="p-4 space-y-3">
                  {releaseNotes.map((note, index) => {
                    const unread = isReleaseNoteUnread(note)
                    return (
                    <button
                      key={note.id || index}
                      type="button"
                      data-testid={`invitee-user-drawer-release-note-${note.id ?? index}`}
                      onClick={() => void handleReleaseNoteSelect(note)}
                      className={`w-full text-left rounded-xl shadow-sm border overflow-hidden transition-colors hover:border-[#6FA8DC]/40 ${getDrawerListItemBackgroundClass(unread)} ${
                        unread ? "border-[#6FA8DC]/30" : "border-gray-200"
                      }`}
                    >
                      <div className="p-4 flex items-start gap-3">
                        <div className="shrink-0 mt-1">
                          {note.version_type === "PROCESS_IMPROVEMENT" && (
                            <TrendingUp className="h-6 w-6 text-blue-600" />
                          )}
                          {note.version_type === "PROCESS_CHANGE" && (
                            <RefreshCw className="h-6 w-6 text-amber-600" />
                          )}
                          {note.version_type === "NEW_FEATURE" && (
                            <Gem className="h-6 w-6 text-green-600" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <h5 className="font-bold text-gray-900 text-left">
                              {note.version_title}
                            </h5>
                            <span className="text-xs text-gray-500 font-medium shrink-0">
                              {note.version_date
                                ? format(
                                    new Date(note.version_date),
                                    "MMM dd, yyyy",
                                  )
                                : ""}
                            </span>
                          </div>
                          <p
                            className={`text-xs font-semibold mt-0.5 ${VERSION_TYPE_COLOR_CLASS[note.version_type] ?? "text-gray-500"}`}
                          >
                            {VERSION_TYPE_LABELS[note.version_type] ??
                              note.version_type}
                          </p>
                          <p className="text-sm text-gray-600 mt-2 line-clamp-3">
                            {getReleaseNoteListPreview(note)}
                          </p>
                        </div>
                        <ChevronRight className="h-5 w-5 text-gray-400 shrink-0 mt-1" />
                      </div>
                    </button>
                    )
                  })}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-gray-400 px-6 text-center">
                  <Gift className="h-12 w-12 stroke-1 mb-3 text-gray-300" />
                  <p className="font-medium text-gray-600">No updates found</p>
                  <p className="text-sm mt-1">
                    Check back later for new features!
                  </p>
                </div>
              )}
            </div>
          ) : view === "announcements" ? (
            /* System Announcements List */
            <div
              data-testid="invitee-user-drawer-announcements-view"
              className="absolute inset-0 overflow-y-auto bg-gray-50/50"
            >
              <div className="bg-white px-4 py-3 border-b border-gray-100 sticky top-0 z-10 shadow-sm">
                <div className="flex items-center justify-between">
                  <h4 className="font-bold text-gray-900">
                    System Announcements
                  </h4>
                  <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                    {announcements.length} Active
                  </span>
                </div>
              </div>

              {loadingAnnouncements ? (
                <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                  <Loader2 className="h-8 w-8 animate-spin mb-2" />
                  <p>Loading announcements...</p>
                </div>
              ) : selectedAnnouncement ? (
                <div className="p-4">
                  <button
                    type="button"
                    data-testid="invitee-user-drawer-announcement-detail-back"
                    onClick={() => {
                      setSelectedAnnouncement(null);
                      void reloadAnnouncements();
                    }}
                    className="mb-4 flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900"
                  >
                    <ChevronRight className="h-4 w-4 rotate-180" />
                    Back to list
                  </button>
                  <div
                    data-testid="invitee-user-drawer-announcement-detail"
                    className="bg-white rounded-xl border border-gray-100 p-4"
                  >
                    <h5 className="font-bold text-gray-900">
                      {selectedAnnouncement.announcement_topic}
                    </h5>
                    <p className="text-xs text-gray-500 mt-1">
                      {selectedAnnouncement.start_date}
                      {selectedAnnouncement.end_date
                        ? ` to ${selectedAnnouncement.end_date}`
                        : ""}
                    </p>
                    {selectedAnnouncement.announcement_description && (
                      <div
                        className="announcement-html text-sm text-gray-700 mt-4 max-w-none"
                        dangerouslySetInnerHTML={{
                          __html: normalizeAnnouncementDescriptionHtml(
                            selectedAnnouncement.announcement_description,
                          ),
                        }}
                      />
                    )}
                  </div>
                </div>
              ) : announcements.length > 0 ? (
                <div className="p-4 space-y-4">
                  {announcements.map((announcement, index) => {
                    const unread = isAnnouncementUnread(announcement)
                    return (
                    <button
                      key={announcement.sa_id || index}
                      type="button"
                      data-testid={`invitee-user-drawer-announcement-item-${announcement.sa_id ?? index}`}
                      onClick={() => handleAnnouncementSelect(announcement)}
                      className={`w-full text-left rounded-xl shadow-sm border overflow-hidden border-l-4 border-l-amber-400 transition-colors hover:border-amber-300 ${getDrawerListItemBackgroundClass(unread)} ${
                        unread ? "border-[#6FA8DC]/30" : "border-gray-200"
                      }`}
                    >
                      <div className="p-4">
                        <h5 className="font-bold text-gray-900">
                          {announcement.announcement_topic}
                        </h5>
                        <p className="text-xs text-gray-500 mt-1">
                          {announcement.start_date}
                          {announcement.end_date
                            ? ` to ${announcement.end_date}`
                            : ""}
                        </p>
                        {announcement.announcement_description && (
                          <div
                            className="text-sm text-gray-700 mt-3 prose prose-sm max-w-none line-clamp-4"
                            dangerouslySetInnerHTML={{
                              __html: announcement.announcement_description,
                            }}
                          />
                        )}
                      </div>
                    </button>
                    )
                  })}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-gray-400 px-6 text-center">
                  <Megaphone className="h-12 w-12 stroke-1 mb-3 text-gray-300" />
                  <p className="font-medium text-gray-600">No announcements</p>
                  <p className="text-sm mt-1">
                    There are no system announcements at this time.
                  </p>
                </div>
              )}
            </div>
          ) : (
            /* Notifications List */
            <div
              data-testid="invitee-user-drawer-notifications-view"
              className="absolute inset-0 overflow-y-auto bg-gray-50/50"
            >
              {!notificationDetail && hasUnreadNotifications(notifications) && (
                <div className="bg-white px-4 py-2 border-b border-gray-100">
                  <div className="flex justify-end">
                    <button
                      type="button"
                      data-testid="invitee-user-drawer-mark-all-read-button"
                      className="text-xs font-bold text-[rgb(21_151_255)] hover:text-[#0d7acc] hover:underline"
                      onClick={() => void handleMarkAllNotificationsRead()}
                    >
                      Mark All As Read
                    </button>
                  </div>
                  <hr className="mt-2 mb-0 border-gray-100" />
                </div>
              )}

              {loadingNotificationDetail ? (
                <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                  <Loader2 className="h-8 w-8 animate-spin mb-2" />
                  <p>Loading notification...</p>
                </div>
              ) : notificationDetail && selectedNotificationMail ? (
                <NotificationDetailPanel
                  mailContent={selectedNotificationMail}
                  detail={notificationDetail}
                  onBack={handleNotificationDetailBack}
                  onCloseDrawer={onClose}
                />
              ) : loadingNotifications ? (
                <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                  <Loader2 className="h-8 w-8 animate-spin mb-2" />
                  <p>Loading notifications...</p>
                </div>
              ) : notifications.length > 0 ? (
                <div className="divide-y divide-gray-100">
                  {notifications.map((notification, index) => (
                    <button
                      key={notification.notification_id || index}
                      type="button"
                      data-testid={`invitee-user-drawer-notification-item-${notification.notification_id ?? index}`}
                      onClick={() => void handleNotificationSelect(notification)}
                      disabled={loadingNotificationDetail}
                      className={`w-full text-left p-4 bg-white hover:bg-gray-50 transition-colors border-l-4 cursor-pointer disabled:opacity-60 disabled:cursor-wait ${isNotificationUnread(notification)
                        ? "border-[rgb(21_151_255)] bg-[rgb(21_151_255)]/10"
                        : "border-transparent"
                        }`}
                    >
                      <div className="flex flex-col gap-1">
                        <p className="text-sm font-bold text-[#344675]">
                          From - {notification.first_name}{" "}
                          {notification.last_name}
                        </p>

                        <p className="text-sm font-bold text-[#344675]">
                          {notification.subject || NOTIFICATION_MENTION_SUBJECT}
                        </p>

                        <p className="text-xs text-gray-400 my-0.5">
                          {notification.created_at
                            ? format(
                              notificationCreatedAtDate(notification.created_at),
                              "yyyy-MM-dd hh:mm:ss a",
                            )
                            : ""}
                        </p>

                        <p className="text-sm text-gray-800 leading-snug">
                          {buildNotificationPreviewText(notification)}
                        </p>
                        {notification.notes_for === "TRAINING" ||
                        notification.notes_for === "MEETING" ? (
                          notification.start_date && (
                            <p className="text-sm text-gray-800 mt-1">
                              <span className="font-bold">
                                Starts On{" "}
                                {formatNotificationScheduleDate(
                                  notification.start_date,
                                  "date",
                                )}{" "}
                                at{" "}
                                {formatNotificationScheduleDate(
                                  notification.start_date,
                                  "time",
                                )}
                              </span>
                            </p>
                          )
                        ) : null}
                        {notification.notes_for === "TODO" &&
                        notification.end_date ? (
                          <p className="text-sm text-gray-800 mt-1">
                            <span className="font-bold">
                              Due On{" "}
                              {formatNotificationScheduleDate(
                                notification.end_date,
                                "date",
                              )}{" "}
                              at{" "}
                              {formatNotificationScheduleDate(
                                notification.end_date,
                                "time",
                              )}
                            </span>
                          </p>
                        ) : null}
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-gray-400 px-6 text-center">
                  <Bell className="h-12 w-12 stroke-1 mb-3 text-gray-300" />
                  <p className="font-medium text-gray-600">No notifications</p>
                  <p className="text-sm mt-1">You're all caught up!</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          data-testid="invitee-user-drawer-footer"
          className="p-4 flex justify-end"
        >
          <button
            type="button"
            data-testid="invitee-user-drawer-accessibility-button"
            className="h-10 w-10 bg-[#344675] hover:bg-[#2c3b63] rounded-full flex items-center justify-center text-white transition-colors shadow-lg"
          >
            <Accessibility className="h-5 w-5" />
          </button>
        </div>
      </div>
      ) : null}

      {/* Confirm Logout — same as applicant sidebar (LR / dashboard roles) */}
      {showLogoutModal &&
        createPortal(
          <div
            data-testid="invitee-user-drawer-logout-modal"
            className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 p-4 sm:p-6 pointer-events-auto"
          >
            <div
              className="relative w-full max-w-md max-h-[min(90vh,calc(100vh-2rem))] overflow-auto rounded-xl bg-white p-4 shadow-xl sm:p-6"
              role="dialog"
              aria-modal="true"
              aria-labelledby="lr-logout-dialog-title"
            >
              <button
                type="button"
                data-testid="invitee-user-drawer-logout-modal-close-button"
                onClick={handleLogoutCancel}
                disabled={isLoggingOut}
                className="absolute right-3 top-3 rounded-md p-1 text-gray-400 transition-colors hover:text-gray-600 disabled:opacity-50 sm:right-4 sm:top-4"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
              <div className="mb-4 flex items-center gap-3 pr-8">
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-red-100">
                  <LogOut className="h-5 w-5 text-red-600" aria-hidden />
                </div>
                <h3
                  id="lr-logout-dialog-title"
                  className="text-base font-bold text-gray-900 sm:text-lg"
                >
                  Confirm Logout
                </h3>
              </div>
              <p className="mb-6 text-sm text-gray-700">
                Are you sure you want to logout? You will need to login again to
                access your account.
              </p>
              <div className="flex flex-col-reverse gap-3 sm:flex-row sm:flex-wrap sm:justify-end">
                <button
                  type="button"
                  data-testid="invitee-user-drawer-logout-cancel-button"
                  onClick={handleLogoutCancel}
                  disabled={isLoggingOut}
                  className="w-full rounded-md border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50 sm:w-auto sm:py-2"
                >
                  No, Cancel
                </button>
                <button
                  type="button"
                  data-testid="invitee-user-drawer-logout-confirm-button"
                  onClick={() => void handleLogoutConfirm()}
                  disabled={isLoggingOut}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-red-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-red-700 disabled:opacity-50 sm:w-auto sm:py-2"
                >
                  {isLoggingOut ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                      Logging out...
                    </>
                  ) : (
                    "Yes, Logout"
                  )}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}

    </div>
  );
}
